import {
  useState,
  useEffect,
  useCallback,
  useContext,
  useMemo,
  useRef
} from 'react';
import {
  Combobox,
  ComboboxButton,
  ComboboxOption,
  ComboboxOptions,
  Dialog,
  DialogPanel,
  DialogBackdrop
} from '@headlessui/react';
import { Search, X, LifeBuoy, Clock, ChevronDown, Loader2 } from 'lucide-react';
import { debounce } from 'lodash';
import { supabase } from '../../utilities/supabase';
import { Badge, FlatButton } from '@core/components';
import { useNavigate } from 'react-router-dom';
import { UserProfileContext } from '../../App';

// Per-category row cap applied by the global_search RPC. Kept in sync with the
// SQL function's LIMIT so we can flag when a category was truncated.
const CATEGORY_RESULT_LIMIT = 50;

// Rows shown per category before collapsing behind a "Show all" toggle.
const COLLAPSE_THRESHOLD = 8;

// Matches the SQL function's input cap.
const MAX_QUERY_LENGTH = 100;

// Recent searches are stored per user; the old key was shared by everyone who
// used the browser.
const RECENT_SEARCHES_KEY_PREFIX = 'recentSearches';
const RECENT_SEARCHES_MAX = 5;

// Groups are ordered by how strong their best match is, then people first.
// Mirrors the SQL function's tie-break order.
const CATEGORY_ORDER = [
  'User Profiles',
  'Leads',
  'Providers',
  'Schemes',
  'Companies',
  'Traces',
  'Pensions',
  'Searches',
  'Admin Profiles',
  'Documents',
  'Tasks',
  'Pipelines',
  'Help Docs',
  'Resources'
];

// >= 90 exact identifier; 50-89 every word matched, or an identifier with a
// close name; 25-49 close spellings, name-only fallbacks and a matched client's
// own records; below 25 weak close spellings.
function rankTier(rank) {
  if (rank >= 90) return 3;
  if (rank >= 50) return 2;
  if (rank >= 25) return 1;
  return 0;
}

function categoryPosition(tableName) {
  const index = CATEGORY_ORDER.indexOf(tableName);
  return index === -1 ? CATEGORY_ORDER.length : index;
}

// A group whose best row is only a guess sorts after firm matches in its tier.
function isWeakGroup(group) {
  return /^(Name only|Similar name|Previous name)/.test(
    group.results[0].data?.matched_on ?? ''
  );
}

function isNameOnly(result) {
  return String(result.data?.matched_on ?? '').startsWith('Name only');
}

// Category filters. Values must match the SQL function's whitelist; 'guides'
// (not 'help') because older UIs send 'help' to mean "everything".
const FILTER_OPTIONS = [
  { value: 'all', label: 'All' },
  { value: 'users', label: 'Users', prefix: 'user:' },
  { value: 'leads', label: 'Leads', prefix: 'lead:' },
  { value: 'traces', label: 'Traces', prefix: 'trace:' },
  { value: 'searches', label: 'Searches', prefix: 'search:' },
  { value: 'pensions', label: 'Pensions', prefix: 'pension:' },
  { value: 'companies', label: 'Companies', prefix: 'company:' },
  { value: 'providers', label: 'Providers', prefix: 'provider:' },
  { value: 'schemes', label: 'Schemes', prefix: 'scheme:' },
  { value: 'documents', label: 'Documents', prefix: 'doc:' },
  { value: 'tasks', label: 'Tasks', prefix: 'task:' },
  { value: 'pipelines', label: 'Pipelines', prefix: 'pipeline:' },
  { value: 'guides', label: 'Help docs', prefix: 'guide:' },
  { value: 'resources', label: 'Resources', prefix: 'resource:' }
];

// Field prefixes are passed through to the RPC, which uses them to force one
// kind of lookup.
const FIELD_PREFIXES = [
  { prefix: 'ni:', label: 'NI number' },
  { prefix: 'tel:', label: 'Phone' },
  { prefix: 'pc:', label: 'Postcode' },
  { prefix: 'dob:', label: 'Date of birth' },
  { prefix: 'policy:', label: 'Policy number' },
  { prefix: 'email:', label: 'Email' },
  { prefix: 'id:', label: 'Any ID' }
];

// Queries that could identify a person (NI, phone, date of birth, postcode,
// email, policy number - anything with a digit or an @, or a field prefix) are
// never kept in recent searches: staff PCs can be shared and the list outlives
// the session. Names are still remembered.
function isSensitiveQuery(query) {
  return /[\d@]/.test(query) || /^(ni|tel|phone|pc|postcode|dob|policy|email|id)\s*:/i.test(query.trim());
}

// Recent searches live in this browser only, one list per user. Saving them to
// the user profile (as BOHSA does) would rewrite the whole user_profiles row on
// every search and fire its automation, audit and sync triggers.
function recentSearchesKey(userId) {
  return userId ? `${RECENT_SEARCHES_KEY_PREFIX}:${userId}` : null;
}

function saveRecentSearches(key, searches) {
  if (!key) return;
  try {
    if (searches.length) {
      localStorage.setItem(key, JSON.stringify(searches));
    } else {
      localStorage.removeItem(key);
    }
  } catch {
    // Storage unavailable (private mode, quota) - recent searches just won't persist.
  }
}

function loadRecentSearches(key) {
  try {
    // The old shared list could hold NI/phone/DOB searches made by anyone who
    // used this browser, so it is deleted rather than migrated.
    localStorage.removeItem(RECENT_SEARCHES_KEY_PREFIX);
    if (!key) return [];
    const saved = JSON.parse(localStorage.getItem(key));
    if (!Array.isArray(saved)) return [];
    const kept = saved.filter(
      s => typeof s === 'string' && !isSensitiveQuery(s)
    );
    if (kept.length !== saved.length) saveRecentSearches(key, kept);
    return kept;
  } catch {
    return [];
  }
}

function removeEveryRecentSearchList() {
  try {
    Object.keys(localStorage)
      .filter(key => key.startsWith(RECENT_SEARCHES_KEY_PREFIX))
      .forEach(key => localStorage.removeItem(key));
  } catch {
    // Storage unavailable - nothing to clear.
  }
}

// Category prefixes, singular and plural. Keep in step with the prefix CASE in
// the global_search SQL function. Field prefixes (ni:, tel:, dob:...) are not
// here: they go to the RPC untouched.
const FILTER_ALIASES = {
  user: 'users',
  users: 'users',
  lead: 'leads',
  leads: 'leads',
  trace: 'traces',
  traces: 'traces',
  search: 'searches',
  searches: 'searches',
  pension: 'pensions',
  pensions: 'pensions',
  company: 'companies',
  companies: 'companies',
  provider: 'providers',
  providers: 'providers',
  scheme: 'schemes',
  schemes: 'schemes',
  doc: 'documents',
  docs: 'documents',
  document: 'documents',
  documents: 'documents',
  task: 'tasks',
  tasks: 'tasks',
  pipeline: 'pipelines',
  pipelines: 'pipelines',
  guide: 'guides',
  guides: 'guides',
  resource: 'resources',
  resources: 'resources'
};

// "company:acme" -> { filter: 'companies', query: 'acme' }
function parsePrefix(query) {
  const match = /^([a-z]+) ?: ?/i.exec(query);
  const filter = match && FILTER_ALIASES[match[1].toLowerCase()];
  return filter
    ? { filter, query: query.slice(match[0].length).trim() }
    : null;
}

// The text that would actually be searched, or null if nothing would run:
// empty, help ("?"), or a single character that isn't an ID digit.
function searchableText(query) {
  const trimmed = (query ?? '').trim();
  if (!trimmed || trimmed.startsWith('?')) return null;
  const prefixed = parsePrefix(trimmed);
  const body = prefixed ? prefixed.query : trimmed;
  return body && (body.length >= 2 || /^\d$/.test(body)) ? body : null;
}

function formatHeader(field) {
  return field.charAt(0).toUpperCase() + field.slice(1).replace(/_/g, ' ');
}

export default function GlobalSearch() {
  const navigate = useNavigate();
  const { userProfile } = useContext(UserProfileContext) || {};
  const recentKey = recentSearchesKey(userProfile?.id);
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchError, setSearchError] = useState(false);
  const [searchFilter, setSearchFilter] = useState('all');
  const [activeIndex, setActiveIndex] = useState(-1);
  const [expandedGroups, setExpandedGroups] = useState({});
  const [recentSearches, setRecentSearches] = useState(() =>
    loadRecentSearches(recentKey)
  );

  // Reload if the signed-in user changes without a remount.
  useEffect(() => {
    setRecentSearches(loadRecentSearches(recentKey));
  }, [recentKey]);

  // Monotonic id so a slow request can't overwrite a newer one's results.
  const searchSeqRef = useRef(0);
  // The in-flight request, cancelled when a newer search starts.
  const abortRef = useRef(null);
  // Latest filter, readable from the (stable) debounced search.
  const searchFilterRef = useRef(searchFilter);
  searchFilterRef.current = searchFilter;

  // The query the current results belong to, so Enter never opens a row from
  // an older search while a newer one is still being typed.
  const resultsQueryRef = useRef('');

  const isHelp = searchQuery.trim().startsWith('?');

  // The next person to sign in on this browser shouldn't see these.
  useEffect(() => {
    const { data } = supabase.auth.onAuthStateChange(event => {
      if (event === 'SIGNED_OUT') removeEveryRecentSearchList();
    });
    return () => data?.subscription?.unsubscribe();
  }, []);

  // Stop any in-flight request when the search box unmounts.
  useEffect(() => () => abortRef.current?.abort(), []);

  const updateRecentSearches = useCallback(
    update => {
      setRecentSearches(current => {
        const next = update(current);
        saveRecentSearches(recentKey, next);
        return next;
      });
    },
    [recentKey]
  );

  const addToRecentSearches = useCallback(
    query => {
      const trimmed = query?.trim();
      if (!searchableText(trimmed) || isSensitiveQuery(trimmed)) return;
      updateRecentSearches(current =>
        [trimmed, ...current.filter(s => s !== trimmed)].slice(
          0,
          RECENT_SEARCHES_MAX
        )
      );
    },
    [updateRecentSearches]
  );

  const removeRecentSearch = useCallback(
    query => updateRecentSearches(current => current.filter(s => s !== query)),
    [updateRecentSearches]
  );

  const clearAllRecentSearches = useCallback(
    () => updateRecentSearches(() => []),
    [updateRecentSearches]
  );

  const performSearch = useCallback(async (query, filterOverride) => {
    // Invalidate any in-flight request: only the latest seq may apply results,
    // and the database stops working on the old one.
    const seq = ++searchSeqRef.current;
    abortRef.current?.abort();
    abortRef.current = null;
    const trimmed = query?.trim() ?? '';

    // filterOverride lets the filter dropdown pass its new value synchronously.
    // A typed prefix ("trace:acme") applies to this query only; it does not
    // change the dropdown, so the next search isn't silently narrowed.
    const prefixed = parsePrefix(trimmed);
    const filter = prefixed?.filter ?? filterOverride ?? searchFilterRef.current;
    const processedQuery = searchableText(trimmed);

    // Nothing to search: empty, help, or a single character (other than an ID).
    if (!processedQuery) {
      resultsQueryRef.current = trimmed;
      setSearchResults([]);
      setSearchError(false);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    const controller = new AbortController();
    abortRef.current = controller;

    try {
      // Raw query - the RPC tokenises it safely, no client-side munging needed.
      const { data, error } = await supabase
        .rpc('global_search', { search_query: processedQuery, filter })
        .abortSignal(controller.signal);

      if (controller.signal.aborted || seq !== searchSeqRef.current) return; // superseded
      if (error) throw error;

      resultsQueryRef.current = trimmed;
      setSearchResults(data || []);
      setSearchError(false);
    } catch (error) {
      if (controller.signal.aborted) return;
      console.error('Error performing search: ', error);
      if (seq === searchSeqRef.current) {
        setSearchResults([]);
        setSearchError(true);
      }
    } finally {
      if (seq === searchSeqRef.current) setIsLoading(false);
    }
  }, []);

  const debouncedSearch = useMemo(
    () => debounce(query => performSearch(query), 300),
    [performSearch]
  );

  useEffect(() => () => debouncedSearch.cancel(), [debouncedSearch]);

  const runSearchNow = useCallback(
    (query, filterOverride) => {
      debouncedSearch.cancel();
      performSearch(query, filterOverride);
    },
    [debouncedSearch, performSearch]
  );

  useEffect(() => {
    const handleKeyDown = e => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setIsOpen(true);
      } else if (e.key === 'Escape') {
        // Clear the results too, so reopening doesn't show the last search
        // under an empty box.
        setSearchQuery('');
        runSearchNow('');
        setIsOpen(false);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [runSearchNow]);

  const handleSearchChange = useCallback(
    e => {
      const value = e.target.value;
      setSearchQuery(value);
      setActiveIndex(-1);
      debouncedSearch(value);
    },
    [debouncedSearch]
  );

  const handleFilterChange = useCallback(
    value => {
      if (!value) return;
      setSearchFilter(value);

      // Drop a typed prefix that now disagrees with the chosen filter, so
      // "company:acme" + Users doesn't silently snap back to Companies.
      const prefixed = parsePrefix(searchQuery.trim());
      const query = prefixed ? prefixed.query : searchQuery;
      if (prefixed) setSearchQuery(query);

      runSearchNow(query, value);
    },
    [searchQuery, runSearchNow]
  );

  // Commit the current query to recent searches and close. Called only on a
  // deliberate action (Enter, opening a result) - never per keystroke, so
  // partial queries don't pollute recent searches.
  const commitAndClose = useCallback(() => {
    addToRecentSearches(searchQuery);
    setIsOpen(false);
  }, [addToRecentSearches, searchQuery]);

  // Ctrl/Cmd-click or middle-click opens the record in a new tab and keeps the
  // search open.
  const goToResult = useCallback(
    (result, newTab = false) => {
      if (!result) return;
      const url = `/${result.path}/${result.data.id}`;
      if (newTab) {
        addToRecentSearches(searchQuery);
        window.open(url, '_blank', 'noopener');
        return;
      }
      commitAndClose();
      navigate(url);
    },
    [addToRecentSearches, commitAndClose, navigate, searchQuery]
  );

  const handleRecentSearchClick = useCallback(
    query => {
      setSearchQuery(query);
      addToRecentSearches(query); // move it back to the top
      runSearchNow(query);
    },
    [addToRecentSearches, runSearchNow]
  );

  const toggleGroup = useCallback(name => {
    setExpandedGroups(prev => ({ ...prev, [name]: !prev[name] }));
  }, []);

  // Group by category, preserving the rank order the RPC returned, and work out
  // each category's columns once.
  const groupedResults = useMemo(() => {
    const groups = [];
    const indexByName = {};
    for (const result of searchResults) {
      const name = result.table_name;
      if (indexByName[name] === undefined) {
        indexByName[name] = groups.length;
        groups.push({ tableName: name, results: [] });
      }
      groups[indexByName[name]].results.push(result);
    }
    // Strongest match first, then firm matches before guesses, then people
    // before records; stable while typing.
    groups.sort(
      (a, b) =>
        rankTier(b.results[0].rank) - rankTier(a.results[0].rank) ||
        isWeakGroup(a) - isWeakGroup(b) ||
        categoryPosition(a.tableName) - categoryPosition(b.tableName)
    );
    return groups.map(group => {
      const first = group.results[0];
      // Older versions of the RPC don't send column_order; fall back to the
      // union of the returned keys.
      const columns = first.column_order?.length
        ? first.column_order
        : [...new Set(group.results.flatMap(r => Object.keys(r.data)))];
      return {
        ...group,
        path: first.path,
        columns: columns.filter(field => !field.endsWith('_path')),
        linkableFields: first.linkable_fields || []
      };
    });
  }, [searchResults]);

  // An identity check (e.g. "smith 12/03/1965") that matched nobody returns
  // name-only rows, labelled by the RPC. Belt and braces: never warn if some
  // person did match the identifier.
  const identityCheckFailed = useMemo(
    () =>
      searchResults.some(isNameOnly) &&
      !searchResults.some(
        result =>
          (result.table_name === 'User Profiles' ||
            result.table_name === 'Leads') &&
          !isNameOnly(result) &&
          result.rank >= 80
      ),
    [searchResults]
  );

  // Apply per-category collapsing for display.
  const visibleGroups = useMemo(
    () =>
      groupedResults.map(group => ({
        ...group,
        visible:
          expandedGroups[group.tableName] ||
          group.results.length <= COLLAPSE_THRESHOLD
            ? group.results
            : group.results.slice(0, COLLAPSE_THRESHOLD)
      })),
    [groupedResults, expandedGroups]
  );

  // Flat, in-display-order list of visible results for keyboard navigation.
  const flatResults = useMemo(
    () => visibleGroups.flatMap(group => group.visible),
    [visibleGroups]
  );

  const flatIndexOf = useMemo(() => {
    const map = new Map();
    flatResults.forEach((result, index) => map.set(result, index));
    return map;
  }, [flatResults]);

  // Reset selection and collapse state whenever the result set changes.
  useEffect(() => {
    setActiveIndex(-1);
    setExpandedGroups({});
  }, [searchResults]);

  // Keep the active index in range if the visible list shrinks (e.g. collapse).
  useEffect(() => {
    setActiveIndex(i => (i >= flatResults.length ? flatResults.length - 1 : i));
  }, [flatResults.length]);

  // Keep the active row in view as you arrow through.
  useEffect(() => {
    if (activeIndex < 0) return;
    document
      .getElementById(`global-search-option-${activeIndex}`)
      ?.scrollIntoView({ block: 'nearest' });
  }, [activeIndex]);

  const handleInputKeyDown = useCallback(
    e => {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setActiveIndex(i => Math.min(i + 1, flatResults.length - 1));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setActiveIndex(i => Math.max(i - 1, flatResults.length ? 0 : -1));
      } else if (e.key === 'Enter') {
        e.preventDefault();
        const resultsAreCurrent =
          resultsQueryRef.current === searchQuery.trim();
        if (resultsAreCurrent && activeIndex >= 0 && flatResults[activeIndex]) {
          goToResult(flatResults[activeIndex], e.ctrlKey || e.metaKey);
        } else {
          addToRecentSearches(searchQuery);
          runSearchNow(searchQuery);
        }
      }
    },
    [
      flatResults,
      activeIndex,
      goToResult,
      addToRecentSearches,
      searchQuery,
      runSearchNow
    ]
  );

  const renderCell = (group, result, field) => {
    const value = result.data[field];
    if (value === null || value === undefined || value === '') {
      return <span className='text-neutral-400'>N/A</span>;
    }

    if (field.includes('date')) {
      const date = new Date(value);
      if (!Number.isNaN(date.getTime())) return date.toLocaleDateString('en-GB');
    }

    // IDs look like links but the whole row is the (single) click target, so
    // there is nothing focusable inside a listbox option.
    if (group.linkableFields.includes(field)) {
      return (
        <span className='text-brand-700 font-medium'>{String(value)}</span>
      );
    }

    return String(value);
  };

  const trimmedQuery = searchQuery.trim();
  const typedPrefix = parsePrefix(trimmedQuery);
  // A typed prefix wins for this query only.
  const effectiveFilter = typedPrefix?.filter ?? searchFilter;
  const filterLabel =
    FILTER_OPTIONS.find(option => option.value === effectiveFilter)?.label ??
    'All';
  const queryWithoutPrefix = typedPrefix ? typedPrefix.query : trimmedQuery;
  const tooShort = Boolean(trimmedQuery) && !isHelp && !searchableText(trimmedQuery);
  const docsTooShort =
    !tooShort && effectiveFilter === 'documents' && queryWithoutPrefix.length < 3;
  const showRecent = !trimmedQuery && recentSearches.length > 0;
  const showResults = Boolean(trimmedQuery) && !isHelp && searchResults.length > 0;
  const showEmpty =
    trimmedQuery && !isHelp && !isLoading && searchResults.length === 0;
  const listboxIds = visibleGroups
    .map((_, groupIndex) => `global-search-list-${groupIndex}`)
    .join(' ');

  // One sentence for screen readers, from the same state as what's on screen.
  let statusText = '';
  if (trimmedQuery && !isHelp && !isLoading) {
    if (searchError) statusText = "The search didn't complete.";
    else if (tooShort) statusText = 'Keep typing - at least 2 characters.';
    else if (searchResults.length === 0 && docsTooShort)
      statusText = 'Type at least 3 characters of the file name.';
    else if (searchResults.length === 0) statusText = 'No results.';
    else
      statusText = `${searchResults.length} results in ${groupedResults.length} categories${
        identityCheckFailed
          ? '. Nobody matches both the name and the details you typed'
          : ''
      }.`;
  }

  return (
    <>
      <FlatButton
        onClick={() => setIsOpen(true)}
        colour='primary'
        size='lg'
        className='lg:text-primary-600 text-white'
        aria-label='Search'
        title='Search (Ctrl+K)'
      >
        <Search size={20} />
      </FlatButton>

      <Dialog
        open={isOpen}
        onClose={() => setIsOpen(false)}
        className='relative z-50'
      >
        <DialogBackdrop className='fixed inset-0 bg-black/30' />

        <div className='fixed inset-0 overflow-y-auto p-4 sm:p-6 md:p-20'>
          <DialogPanel className='mx-auto max-w-3xl transform divide-y divide-gray-100 overflow-visible rounded-xl bg-white shadow-2xl ring-1 ring-black ring-opacity-5'>
            <div className='relative flex'>
              <Search className='pointer-events-none absolute left-3 top-3.5 h-5 w-5 text-gray-400' />
              <input
                id='global-search-input'
                autoFocus
                type='text'
                role='combobox'
                autoComplete='off'
                spellCheck={false}
                aria-label='Search the CRM'
                aria-expanded={showResults}
                aria-controls={listboxIds || undefined}
                aria-activedescendant={
                  activeIndex >= 0
                    ? `global-search-option-${activeIndex}`
                    : undefined
                }
                className='h-12 w-full border-0 bg-transparent pl-11 pr-10 text-gray-900 focus:ring-0'
                placeholder='Name, NI number, phone, postcode, policy number, ID...'
                maxLength={MAX_QUERY_LENGTH}
                onChange={handleSearchChange}
                value={searchQuery}
                onKeyDown={handleInputKeyDown}
              />
              {isLoading && (
                <Loader2 className='animate-spin h-5 w-5 text-gray-400 absolute right-44 top-3.5' />
              )}
              <div className='relative shrink-0'>
                <Combobox value={searchFilter} onChange={handleFilterChange}>
                  <ComboboxButton
                    aria-label='Search in'
                    className='relative w-36 cursor-pointer bg-white py-3 pl-3 pr-9 text-left border-l border-gray-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-opacity-75 sm:text-sm rounded-tr-xl'
                  >
                    <span className='block truncate font-medium text-right'>
                      {filterLabel}
                    </span>
                    <span className='pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2'>
                      <ChevronDown
                        className='h-4 w-4 text-gray-500'
                        aria-hidden='true'
                      />
                    </span>
                  </ComboboxButton>
                  <ComboboxOptions className='absolute right-0 mt-1 max-h-72 w-48 overflow-auto rounded-md bg-white py-1 text-sm shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none z-[9999]'>
                    {FILTER_OPTIONS.map(option => (
                      <ComboboxOption
                        key={option.value}
                        value={option.value}
                        className='relative cursor-pointer select-none py-2 px-4 text-gray-700 data-[focus]:bg-gray-100 data-[focus]:text-gray-800'
                      >
                        {({ selected }) => (
                          <span
                            className={`block truncate ${
                              selected ? 'font-medium' : 'font-normal'
                            }`}
                          >
                            {option.label}
                          </span>
                        )}
                      </ComboboxOption>
                    ))}
                  </ComboboxOptions>
                </Combobox>
              </div>
            </div>

            {showRecent && (
              <div className='p-2 overflow-hidden'>
                <div className='flex justify-between items-center px-2 py-1'>
                  <h4 className='text-xs font-medium text-gray-500 flex items-center gap-1'>
                    <Clock size={12} /> Recent searches
                  </h4>
                  <button
                    type='button'
                    onClick={clearAllRecentSearches}
                    className='text-xs text-gray-500 hover:text-gray-700'
                  >
                    Clear all
                  </button>
                </div>
                {recentSearches.map(search => (
                  <div
                    key={search}
                    className='flex items-center justify-between group'
                  >
                    <button
                      type='button'
                      className='flex-grow text-left px-2 py-1.5 text-sm text-gray-700 hover:bg-gray-100 rounded'
                      onClick={() => handleRecentSearchClick(search)}
                    >
                      {search}
                    </button>
                    <button
                      type='button'
                      aria-label={`Remove "${search}" from recent searches`}
                      onClick={e => {
                        e.stopPropagation();
                        removeRecentSearch(search);
                      }}
                      className='px-2 text-gray-400 hover:text-gray-600 opacity-0 group-hover:opacity-100 max-lg:opacity-100 focus:opacity-100'
                    >
                      <X size={14} />
                    </button>
                  </div>
                ))}
              </div>
            )}

            <div aria-live='polite' className='sr-only'>
              {statusText}
            </div>

            {identityCheckFailed && showResults && (
              <div
                role='alert'
                className='mx-4 mt-4 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900'
              >
                Nobody matches both the name and the details you typed. The
                results below match the name only - confirm who you&apos;re
                speaking to before going further.
              </div>
            )}

            {showResults && (
              <div
                id='global-search-results'
                className='max-h-[32rem] overflow-y-auto p-4 space-y-6'
              >
                {visibleGroups.map((group, groupIndex) => (
                  <section key={group.tableName}>
                    <h3
                      id={`global-search-group-${groupIndex}`}
                      className='text-sm font-semibold text-base-900 mb-2'
                    >
                      {group.tableName}
                      <span className='ml-2 text-xs font-normal text-base-500'>
                        {group.results.length >= CATEGORY_RESULT_LIMIT
                          ? `${CATEGORY_RESULT_LIMIT}+`
                          : group.results.length}
                      </span>
                    </h3>
                    <div
                      id={`global-search-list-${groupIndex}`}
                      role='listbox'
                      aria-labelledby={`global-search-group-${groupIndex}`}
                      className='overflow-x-auto bg-white rounded-md w-full'
                    >
                      <table
                        role='presentation'
                        className='w-full divide-y divide-neutral-200'
                      >
                        <thead aria-hidden='true'>
                          <tr>
                            {group.columns.map(field => (
                              <th
                                key={field}
                                className='py-2 px-2 text-xs text-left whitespace-nowrap font-semibold text-neutral-700'
                              >
                                {formatHeader(field)}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody className='divide-y divide-neutral-200'>
                          {group.visible.map(result => {
                            const index = flatIndexOf.get(result);
                            const active = index === activeIndex;
                            return (
                              <tr
                                key={result.table_id}
                                id={`global-search-option-${index}`}
                                role='option'
                                aria-selected={active}
                                aria-label={[
                                  group.tableName,
                                  result.data.name ??
                                    result.data.title ??
                                    result.data.file_name ??
                                    result.data.id,
                                  result.data.matched_on
                                ]
                                  .filter(Boolean)
                                  .join(', ')}
                                onClick={e =>
                                  goToResult(result, e.ctrlKey || e.metaKey)
                                }
                                onAuxClick={e => {
                                  if (e.button !== 1) return;
                                  e.preventDefault();
                                  goToResult(result, true);
                                }}
                                className={`cursor-pointer ${
                                  active
                                    ? 'bg-neutral-200 ring-1 ring-inset ring-neutral-300'
                                    : 'hover:bg-neutral-100'
                                }`}
                              >
                                {group.columns.map(field => {
                                  const value = result.data[field];
                                  return (
                                    <td
                                      key={field}
                                      className='py-1 px-2 text-xs text-neutral-700'
                                    >
                                      <div
                                        className='max-w-xs truncate'
                                        title={
                                          typeof value === 'string' &&
                                          !field.includes('date')
                                            ? value
                                            : undefined
                                        }
                                      >
                                        {renderCell(group, result, field)}
                                      </div>
                                    </td>
                                  );
                                })}
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                    {group.results.length > COLLAPSE_THRESHOLD && (
                      <button
                        type='button'
                        onClick={() => toggleGroup(group.tableName)}
                        className='mt-1 text-xs font-medium text-brand-700 hover:text-brand-800'
                      >
                        {expandedGroups[group.tableName]
                          ? 'Show less'
                          : `Show all ${group.results.length}`}
                      </button>
                    )}
                    {group.results.length >= CATEGORY_RESULT_LIMIT && (
                      <p className='mt-2 text-xs text-base-500 italic'>
                        Showing the top {CATEGORY_RESULT_LIMIT} matches. Add
                        more words to narrow the results.
                      </p>
                    )}
                  </section>
                ))}
              </div>
            )}

            {isHelp && (
              <div className='p-6 space-y-4 text-center'>
                <div className='flex justify-center'>
                  <LifeBuoy size={20} className='text-base-400' />
                </div>
                <h3 className='text-base font-medium text-base-900'>
                  Help with searching
                </h3>
                <div className='text-sm text-base-600 space-y-3'>
                  <p>
                    Type a name, NI number, phone number, postcode, date of
                    birth, email, policy number, company number or any ID.
                    Every word must match, spacing and capitals don&apos;t
                    matter, and close spellings are found too (&quot;smyth&quot;
                    finds &quot;Smith&quot;).
                  </p>
                  <div className='space-y-1'>
                    {[
                      ['smith 12/03/1965', 'Name and date of birth'],
                      ['AB 12 34 56 C', 'NI number'],
                      ['07700 900123', 'Phone number'],
                      ['SW1A 1AA', 'Postcode']
                    ].map(([example, meaning]) => (
                      <p key={example}>
                        <span className='font-medium bg-base-100 px-2 py-0.5 rounded'>
                          {example}
                        </span>
                        <span className='ml-2 text-base-500'>{meaning}</span>
                      </p>
                    ))}
                  </div>
                  <p>Search one kind of detail:</p>
                  <div className='flex flex-wrap justify-center gap-2'>
                    {FIELD_PREFIXES.map(({ prefix, label }) => (
                      <span
                        key={prefix}
                        title={label}
                        className='font-medium bg-base-100 px-2 py-0.5 rounded text-xs'
                      >
                        {prefix}
                      </span>
                    ))}
                  </div>
                  <p>Or one area (same as the dropdown):</p>
                  <div className='flex flex-wrap justify-center gap-2'>
                    {FILTER_OPTIONS.filter(o => o.prefix).map(option => (
                      <span
                        key={option.value}
                        title={option.label}
                        className='font-medium bg-base-100 px-2 py-0.5 rounded text-xs'
                      >
                        {option.prefix}
                      </span>
                    ))}
                  </div>
                  <p className='text-xs text-base-500'>
                    Documents are only searched with <Badge>doc:</Badge> or the
                    Documents filter. Searches containing numbers aren&apos;t
                    saved to recent searches.
                  </p>
                  <p className='text-xs text-base-400 italic pt-2'>
                    Tip: press <Badge>Ctrl</Badge> + <Badge>K</Badge> to open
                    search from anywhere
                  </p>
                </div>
              </div>
            )}

            {showEmpty && (
              <div className='p-6 text-center'>
                {searchError ? (
                  <p className='text-sm text-red-600'>
                    The search didn&apos;t complete. Check your connection and
                    press Enter to try again.
                  </p>
                ) : tooShort ? (
                  <p className='text-sm text-base-500'>
                    Keep typing - at least 2 characters.
                  </p>
                ) : docsTooShort ? (
                  <p className='text-sm text-base-500'>
                    Type at least 3 characters of the file name, or a document
                    ID.
                  </p>
                ) : (
                  <p className='text-sm text-base-500'>
                    No results for &quot;{trimmedQuery}&quot;
                    {effectiveFilter !== 'all' && ` in ${filterLabel}`}.
                  </p>
                )}
              </div>
            )}

            <div className='flex items-center justify-between gap-x-4 p-3 border-t border-gray-50 bg-base-50 rounded-b-xl'>
              <div className='flex flex-wrap gap-x-3 gap-y-1'>
                <span className='text-xs text-base-600'>
                  <Badge>↑↓</Badge> to navigate
                </span>
                <span className='text-xs text-base-600'>
                  <Badge>↵</Badge> to open
                </span>
                <span className='text-xs text-base-600'>
                  <Badge>?</Badge> for help
                </span>
              </div>
              <span className='text-xs text-base-600 whitespace-nowrap'>
                Searching: {filterLabel}
              </span>
            </div>
          </DialogPanel>
        </div>
      </Dialog>
    </>
  );
}
